import { type FC, useCallback, useMemo, useRef, useState } from 'react';

import Banner from '@/components/UI/Banner';
import Switch from '@/components/UI/FormElements/Switch/Switch';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import type {
  DosingMatrixRow,
  DosingMatrixRowKey,
  DosingSessionDraft,
  TreatmentOption,
} from '@/components/Samples/SamplePlanTable/Execute/ExecuteSamplesSetDosingStep.utils';
import {
  blankCalculationBaseWarning,
  blankRowNames,
  createSessionId,
  sessionNameError,
  sessionTreatmentsError,
} from '@/components/Samples/SamplePlanTable/Execute/ExecuteSamplesSetDosingStep.utils';
import { errorToast } from '@/helpers';
import { _isEmpty, _isNil, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import useElementBinding from '@/support/Hooks/utils/useElementBinding';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

import { buildGridRows, collectGridTreatments, type DosingSessionGridRow } from './DosingSession.utils';
import DosingSessionTable from './DosingSessionTable';

export interface DosingSessionModalProps {
  /** Session being edited; absent when adding a new one. */
  session?: DosingSessionDraft;
  /** Every session on the plan, used to keep session names unique. */
  sessions: Array<DosingSessionDraft>;
  rows: Array<DosingMatrixRow>;
  rowOptions: Map<DosingMatrixRowKey, Array<TreatmentOption>>;
  defaultName: string;
  onSave: (session: DosingSessionDraft) => void;
  closeModal: () => void;
}

/**
 * Authors a single dosing session: a name, whether collection times are calculated from it, and one
 * treatment per study group. Performs no I/O — the whole plan is submitted with the sampling sheet,
 * so the session is handed back to the wizard step as a draft.
 */
const DosingSessionModal: FC<DosingSessionModalProps> = ({
  session,
  sessions,
  rows,
  rowOptions,
  defaultName,
  onSave,
  closeModal,
}) => {
  const isOnlySession = _isNil(session) ? _isEmpty(sessions) : sessions.length === 1;
  const [name, setName] = useState(session?.name ?? defaultName);
  const [nameTouched, setNameTouched] = useState(false);
  const [isCalculationBase, setIsCalculationBase] = useState(
    isOnlySession ? true : (session?.isCalculationBase ?? false)
  );
  const hotRef = useRef<HotTableRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useElementBinding({
    field: 'hot',
    element: containerRef,
    accessor: useCallback(() => hotRef.current?.hotInstance, []),
  });

  const gridRows = useMemo(() => buildGridRows(rows, rowOptions, session), [rows, rowOptions, session]);
  const getTreatmentOptions = useCallback((rowKey: DosingMatrixRowKey) => rowOptions.get(rowKey) ?? [], [rowOptions]);

  const initialTreatments = collectGridTreatments(gridRows, rows, rowOptions);
  const [blankRows, setBlankRows] = useState(() => blankRowNames(rows, initialTreatments));
  const [treatmentsError, setTreatmentsError] = useState(() => sessionTreatmentsError(initialTreatments));

  const handleGridChange = useCallback(() => {
    const sourceData = hotRef.current?.hotInstance?.getSourceData() as Array<DosingSessionGridRow> | undefined;
    if (_notNil(sourceData)) {
      const treatments = collectGridTreatments(sourceData, rows, rowOptions);
      setBlankRows(blankRowNames(rows, treatments));
      setTreatmentsError(sessionTreatmentsError(treatments));
    }
  }, [rows, rowOptions]);

  const nameError = sessionNameError(name, sessions, session?.id);
  const trimmedName = name.trim();

  const handleSave = useCallback(() => {
    if (_notNil(nameError)) {
      setNameTouched(true);
      return;
    }

    const hotInstance = hotRef.current?.hotInstance;
    if (_isNil(hotInstance)) {
      errorToast('Unable to access table data');
      return;
    }

    const sourceData = hotInstance.getSourceData() as Array<DosingSessionGridRow>;
    const treatments = collectGridTreatments(sourceData, rows, rowOptions);
    const nextTreatmentsError = sessionTreatmentsError(treatments);
    if (_notNil(nextTreatmentsError)) {
      setTreatmentsError(nextTreatmentsError);
      return;
    }
    onSave({
      id: session?.id ?? createSessionId(),
      name: name.trim(),
      isCalculationBase: isOnlySession ? true : isCalculationBase,
      treatments,
    });
    closeModal();
  }, [nameError, session, name, isCalculationBase, isOnlySession, rows, rowOptions, onSave, closeModal]);

  return (
    <ModalContainer size="medium" className="bg-white" testKey="DosingSessionModal">
      <ModalHeader
        title={_isNil(session) ? 'Add dosing session' : 'Edit dosing session'}
        closeModal={closeModal}
        className="pa3 bb b--moon-gray w-100"
      />

      <div className="pa3" ref={containerRef} data-testid="dosing-session__body">
        <label htmlFor="dosing-session-name">Session name</label>
        <input
          type="text"
          id="dosing-session-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => setNameTouched(true)}
          style={{ marginBottom: 0 }}
          data-testid="dosing-session__name-input"
          autoFocus
        />
        {nameTouched && _notNil(nameError) && (
          <small className="red db pt2" data-testid="dosing-session__name-error">
            {nameError}
          </small>
        )}

        <Banner info dismiss={false} className="mt3">
          <div className="flex justify-between items-center">
            <div className="mr3">
              <h3 className="f5 normal lh-title">Use for collection-time calculation</h3>
              <p className="f6 pt2 lh-copy">
                {isOnlySession
                  ? 'This plan has only one session, so collection times must be calculated from it.'
                  : "Sample collection times on the sheet are calculated from this session's dosing time. Only one session can be the basis."}
              </p>
            </div>
            <Switch
              className="flex-none"
              value={isCalculationBase}
              onChange={setIsCalculationBase}
              disabled={isOnlySession}
              ariaLabel="Use for collection-time calculation"
              testId="dosing-session__calculation-base"
            />
          </div>
        </Banner>

        {_notNil(treatmentsError) && (
          <Banner critical dismiss={false} className="mt3" testId="dosing-session__treatments-error">
            <p className="f6 lh-copy">{treatmentsError}</p>
          </Banner>
        )}

        {_isNil(treatmentsError) && isCalculationBase && _isNotEmpty(blankRows) && (
          <Banner warning dismiss={false} className="mt3" testId="dosing-session__blank-treatments-warning">
            <p className="f6 lh-copy">
              {blankCalculationBaseWarning(_isNotBlank(trimmedName) ? trimmedName : 'this session', blankRows)}
            </p>
          </Banner>
        )}

        <div className="mt3">
          <DosingSessionTable
            rows={rows}
            gridRows={gridRows}
            getTreatmentOptions={getTreatmentOptions}
            sessionName={name}
            onGridChange={handleGridChange}
            innerRef={hotRef}
          />
        </div>
      </div>

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={handleSave}
        onCancel={closeModal}
        submitBtnText="Save"
        cancelBtnText="Cancel"
        submitButtonProps={{
          disabled: _notNil(nameError) || _notNil(treatmentsError),
          testId: 'dosing-session__submit',
        }}
        cancelButtonProps={{ testId: 'dosing-session__cancel' }}
      />
    </ModalContainer>
  );
};

export default DosingSessionModal;
